// client/src/components/admin/league/LeagueStatusManagementPanel.js
import React, { useState } from 'react'
import { LEAGUE_STATUS, formatStatus } from '../../../constants/leagueStatuses'
import ConfirmationModal from '../../common/ConfirmationModal'
import './LeagueStatusManagementPanel.css'

/**
 * Get visual information (icon, color, description) for a league status
 * @param {string} status - League status
 * @returns {object} Status info with icon, color, and description
 */
const getStatusInfo = (status) => {
  const statusInfo = {
    [LEAGUE_STATUS.DRAFT]: {
      icon: '📝',
      color: '#6c757d',
      description: 'League is being set up',
    },
    [LEAGUE_STATUS.REGISTRATION_OPEN]: {
      icon: '🚪',
      color: '#0dcaf0',
      description: 'Players can register',
    },
    [LEAGUE_STATUS.REGISTRATION_CLOSED]: {
      icon: '🔒',
      color: '#ffc107',
      description: 'Registration ended, creating divisions',
    },
    [LEAGUE_STATUS.PENDING_START]: {
      icon: '⏳',
      color: '#0d6efd',
      description: 'Divisions created, ready to start',
    },
    [LEAGUE_STATUS.ACTIVE]: {
      icon: '🏆',
      color: '#198754',
      description: 'League is active',
    },
    [LEAGUE_STATUS.IN_PROGRESS]: {
      icon: '🎾',
      color: '#198754',
      description: 'Matches in progress',
    },
    [LEAGUE_STATUS.PLAYOFFS_SETUP]: {
      icon: '🔧',
      color: '#ffc107',
      description: 'Bracket setup - not visible to users',
    },
    [LEAGUE_STATUS.PLAYOFFS]: {
      icon: '👑',
      color: '#2563eb',
      description: 'Playoff matches',
    },
    [LEAGUE_STATUS.COMPLETED]: {
      icon: '✅',
      color: '#495057',
      description: 'League finished',
    },
    [LEAGUE_STATUS.CANCELED]: {
      icon: '❌',
      color: '#dc3545',
      description: 'League canceled',
    },
  }
  return statusInfo[status] || statusInfo[LEAGUE_STATUS.DRAFT]
}

/**
 * Get the next status in the league lifecycle
 * @param {string} currentStatus - Current league status
 * @returns {string|undefined} Next status or undefined if no transition available
 */
const getNextStatus = (currentStatus) => {
  const transitions = {
    [LEAGUE_STATUS.DRAFT]: LEAGUE_STATUS.REGISTRATION_OPEN,
    [LEAGUE_STATUS.REGISTRATION_OPEN]: LEAGUE_STATUS.REGISTRATION_CLOSED,
    [LEAGUE_STATUS.REGISTRATION_CLOSED]: LEAGUE_STATUS.PENDING_START,
    [LEAGUE_STATUS.PENDING_START]: LEAGUE_STATUS.ACTIVE,
    [LEAGUE_STATUS.ACTIVE]: LEAGUE_STATUS.PLAYOFFS_SETUP,
    [LEAGUE_STATUS.IN_PROGRESS]: LEAGUE_STATUS.PLAYOFFS_SETUP,
    [LEAGUE_STATUS.PLAYOFFS_SETUP]: LEAGUE_STATUS.PLAYOFFS,
    [LEAGUE_STATUS.PLAYOFFS]: LEAGUE_STATUS.COMPLETED,
  }
  return transitions[currentStatus]
}

/**
 * Get the button text for transitioning to the next status
 * @param {string} currentStatus - Current league status
 * @returns {string} Button text for the next action
 */
const getNextStatusText = (currentStatus) => {
  const messages = {
    [LEAGUE_STATUS.DRAFT]: 'Open Registration',
    [LEAGUE_STATUS.REGISTRATION_OPEN]: 'Close Registration',
    [LEAGUE_STATUS.REGISTRATION_CLOSED]: 'Publish Divisions',
    [LEAGUE_STATUS.PENDING_START]: 'Start League',
    [LEAGUE_STATUS.ACTIVE]: 'Start Playoffs',
    [LEAGUE_STATUS.IN_PROGRESS]: 'Start Playoffs',
    [LEAGUE_STATUS.PLAYOFFS_SETUP]: 'Publish Bracket',
    [LEAGUE_STATUS.PLAYOFFS]: 'Complete League',
  }
  return messages[currentStatus]
}

/**
 * Check if a league can transition to the next status
 * @param {object} league - League object
 * @returns {boolean} True if transition is available
 */
const canTransitionStatus = (league) => {
  const nextStatus = getNextStatus(league.league_status)
  return nextStatus !== undefined
}

/**
 * Get the CSS class for a status badge
 * @param {string} status - League status
 * @returns {string} Bootstrap badge class
 */
const getStatusBadgeClass = (status) => {
  const statusClasses = {
    [LEAGUE_STATUS.DRAFT]: 'bg-secondary',
    [LEAGUE_STATUS.REGISTRATION_OPEN]: 'bg-info',
    [LEAGUE_STATUS.REGISTRATION_CLOSED]: 'bg-warning',
    [LEAGUE_STATUS.PENDING_START]: 'bg-primary',
    [LEAGUE_STATUS.ACTIVE]: 'bg-success',
    [LEAGUE_STATUS.IN_PROGRESS]: 'bg-success',
    [LEAGUE_STATUS.PLAYOFFS_SETUP]: 'bg-warning',
    [LEAGUE_STATUS.PLAYOFFS]: 'bg-purple',
    [LEAGUE_STATUS.COMPLETED]: 'bg-dark',
    [LEAGUE_STATUS.CANCELED]: 'bg-danger',
  }
  return statusClasses[status] || 'bg-secondary'
}

/**
 * Check if the current status is a playoffs-transition status
 * @param {string} currentStatus - current league status
 * @returns {boolean}
 */
const isPlayoffsTransitionStatus = (currentStatus) =>
  currentStatus === LEAGUE_STATUS.ACTIVE ||
  currentStatus === LEAGUE_STATUS.IN_PROGRESS

/**
 * LeagueStatusManagementPanel - Displays league status, timeline, and transition actions
 *
 * @param {object} props
 * @param {object} props.league - League object with league_status property
 * @param {function} props.onStatusTransition - Callback when status transition is triggered
 * @param {function} [props.onSkipPlayoffs] - Callback when skip playoffs is confirmed
 */
export default function LeagueStatusManagementPanel({
  league,
  onStatusTransition,
  onSkipPlayoffs,
}) {
  const [showSkipConfirm, setShowSkipConfirm] = useState(false)

  if (!league) return null

  const statusInfo = getStatusInfo(league.league_status)
  const canTransition = canTransitionStatus(league)
  const showSkipButton =
    isPlayoffsTransitionStatus(league.league_status) && onSkipPlayoffs

  // Timeline statuses (standard progression)
  const timelineStatuses = [
    LEAGUE_STATUS.DRAFT,
    LEAGUE_STATUS.REGISTRATION_OPEN,
    LEAGUE_STATUS.REGISTRATION_CLOSED,
    LEAGUE_STATUS.PENDING_START,
    LEAGUE_STATUS.ACTIVE,
    LEAGUE_STATUS.PLAYOFFS,
    LEAGUE_STATUS.COMPLETED,
  ]

  return (
    <>
      <div className="card mb-4">
        <div className="card-body">
          <h4 className="card-title">
            <i className="fas fa-chart-line me-2"></i>
            League Status Management
          </h4>

          {/* Current Status Display */}
          <div className="current-status-display mb-4">
            <div className="current-status-layout d-flex align-items-center">
              <div className="status-icon me-4" aria-hidden="true">
                {statusInfo.icon}
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-2">
                  <span
                    className={`badge ${getStatusBadgeClass(league.league_status)} me-3`}
                    aria-label={`Current status: ${formatStatus(league.league_status)}`}
                  >
                    {formatStatus(league.league_status)}
                  </span>
                  <span className="text-muted small">Current Phase</span>
                </div>
                <p className="mb-0 text-muted">{statusInfo.description}</p>
              </div>
              {(canTransition || showSkipButton) && (
                <div className="status-action-buttons ms-3 d-flex gap-2">
                  {showSkipButton && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setShowSkipConfirm(true)}
                      data-track="admin.league_skip_playoffs"
                      aria-label="Skip playoffs and mark league as completed"
                    >
                      Skip Playoffs
                    </button>
                  )}
                  {canTransition && (
                    <button
                      className="btn btn-success"
                      onClick={() => onStatusTransition(league)}
                      data-track="admin.league_status_transition"
                      aria-label={`Transition to ${getNextStatusText(league.league_status)}`}
                    >
                      <i className="fas fa-play me-2" aria-hidden="true"></i>
                      {getNextStatusText(league.league_status)}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status Timeline */}
          <div className="status-timeline">
            <div className="timeline-header">
              <h6 className="mb-0">
                <i className="fas fa-route me-2" aria-hidden="true"></i>
                League Progress Timeline
              </h6>
              <p className="text-muted small mb-0 mt-1">
                Track your league's journey from setup to completion
              </p>
            </div>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {timelineStatuses.map((status, index, array) => {
                const isCurrentStatus = league.league_status === status
                const isCompletedStatus =
                  array.indexOf(league.league_status) > index
                const info = getStatusInfo(status)

                return (
                  <div
                    key={status}
                    className={`timeline-step ${isCurrentStatus ? 'current' : ''} ${
                      isCompletedStatus ? 'completed' : ''
                    }`}
                    style={{
                      opacity: isCurrentStatus || isCompletedStatus ? 1 : 0.5,
                    }}
                    title={info.description}
                    aria-label={`${formatStatus(status)}: ${info.description}${
                      isCurrentStatus ? ' (current)' : ''
                    }${isCompletedStatus ? ' (completed)' : ''}`}
                  >
                    <div className="timeline-icon" aria-hidden="true">
                      {isCompletedStatus ? '✓' : info.icon}
                    </div>
                    <div className="timeline-label">{formatStatus(status)}</div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Next Action Info */}
          <div
            className="mt-4 p-3"
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '12px',
              border: '1px solid #0ea5e9',
            }}
            role="status"
            aria-live="polite"
          >
            <div className="d-flex align-items-center">
              <i
                className="fas fa-info-circle text-info me-2"
                aria-hidden="true"
              ></i>
              <small className="text-info fw-semibold">
                {canTransition
                  ? `Next: ${getNextStatusText(league.league_status)}`
                  : league.league_status === LEAGUE_STATUS.COMPLETED
                    ? 'League has completed successfully! 🎉'
                    : league.league_status === LEAGUE_STATUS.CANCELED
                      ? 'League was canceled'
                      : 'League is in its final state'}
              </small>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showSkipConfirm}
        title="Skip Playoffs?"
        message={`Are you sure you want to skip playoffs for "${league.league_name}"? This will mark the league as completed without running playoffs. This action cannot be undone.`}
        confirmLabel="Yes, Skip Playoffs"
        confirmVariant="warning"
        onConfirm={() => {
          setShowSkipConfirm(false)
          onSkipPlayoffs(league)
        }}
        onClose={() => setShowSkipConfirm(false)}
      />
    </>
  )
}
